import React, { useState } from 'react';
import { Container, Row, Col, Badge, Modal, Button } from 'react-bootstrap';

const Services = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      id: 1,
      icon: '🔥',
      title: 'Thermal System Design',
      category: 'Engineering',
      description: 'Custom thermal system design and engineering for industrial applications with advanced modeling and simulation capabilities.',
      features: ['3D Modeling', 'Heat Transfer Analysis', 'Energy Optimization', 'Custom Solutions'],
      benefits: ['Reduced Energy Costs', 'Improved Efficiency', 'Longer Equipment Life', 'Compliance Assurance'],
      process: ['Initial Consultation', 'Site Survey', 'Design Development', 'Testing & Validation']
    },
    {
      id: 2,
      icon: '⚙️',
      title: 'Installation & Commissioning',
      category: 'Implementation',
      description: 'Professional installation and commissioning services ensuring optimal performance and compliance with industry standards.',
      features: ['Expert Installation', 'System Integration', 'Performance Testing', 'Documentation'],
      benefits: ['Quick Setup', 'Guaranteed Performance', 'Minimal Downtime', 'Full Support'],
      process: ['Pre-Installation Planning', 'Equipment Installation', 'System Testing', 'Final Commissioning']
    },
    {
      id: 3,
      icon: '🔧',
      title: 'Maintenance & Support',
      category: 'Support',
      description: 'Comprehensive maintenance programs and 24/7 technical support to maximize system uptime and efficiency.',
      features: ['Preventive Maintenance', '24/7 Support', 'Remote Monitoring', 'Spare Parts'],
      benefits: ['Maximum Uptime', 'Extended Life', 'Cost Savings', 'Peace of Mind'],
      process: ['Maintenance Planning', 'Regular Inspections', 'Preventive Actions', 'Emergency Response']
    },
    {
      id: 4,
      icon: '📊',
      title: 'Performance Optimization',
      category: 'Enhancement',
      description: 'Advanced analytics and optimization services to enhance system performance and reduce energy consumption.',
      features: ['Data Analytics', 'Performance Monitoring', 'Efficiency Improvements', 'Cost Reduction'],
      benefits: ['Lower Operating Costs', 'Better Performance', 'Environmental Benefits', 'ROI Improvement'],
      process: ['Performance Assessment', 'Data Analysis', 'Optimization Plan', 'Implementation']
    },
    {
      id: 5,
      icon: '🛡️',
      title: 'Safety & Compliance',
      category: 'Safety',
      description: 'Safety audits and compliance services ensuring adherence to international thermal safety standards.',
      features: ['Safety Audits', 'Compliance Checks', 'Risk Assessment', 'Certification Support'],
      benefits: ['Regulatory Compliance', 'Risk Mitigation', 'Insurance Benefits', 'Worker Safety'],
      process: ['Safety Assessment', 'Gap Analysis', 'Compliance Planning', 'Certification']
    },
    {
      id: 6,
      icon: '🌡️',
      title: 'Temperature Control Systems',
      category: 'Control Systems',
      description: 'Precision temperature control solutions for critical industrial processes and manufacturing operations.',
      features: ['Precision Control', 'Real-time Monitoring', 'Automated Systems', 'Data Logging'],
      benefits: ['Process Consistency', 'Quality Improvement', 'Reduced Waste', 'Automation Benefits'],
      process: ['Requirements Analysis', 'System Design', 'Installation', 'Calibration']
    }
  ];

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setShowModal(true);
  };

  const ServiceCard = ({ service }) => (
    <Col lg={6} xl={4} className="mb-4">
      <div className="service-card h-100" onClick={() => handleServiceClick(service)} style={{ cursor: 'pointer' }}>
        <div className="text-center mb-3">
          <span className="service-icon" style={{ 
            fontSize: '2.5rem', 
            display: 'block', 
            marginBottom: '1rem',
            color: '#64748b'
          }}>
            {service.icon}
          </span>
          <Badge bg="primary" className="mb-2">{service.category}</Badge>
        </div>
        
        <h4>{service.title}</h4>
        <p>{service.description}</p>
        
        <div className="mt-3">
          <h6 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b' }}>Key Features:</h6>
          {service.features.slice(0, 2).map((feature, idx) => (
            <Badge bg="secondary" className="me-2 mb-2" key={idx} style={{ fontSize: '0.75rem' }}>
              ✓ {feature}
            </Badge>
          ))}
          {service.features.length > 2 && (
            <Badge bg="light" text="dark" className="me-2 mb-2" style={{ fontSize: '0.75rem' }}>
              +{service.features.length - 2} more
            </Badge>
          )}
        </div>
        
        <div className="mt-3">
          <Button variant="outline-primary" size="sm">
            Learn More →
          </Button>
        </div>
      </div>
    </Col>
  );

  return (
    <>
      <div className="page-header">
        <Container>
          <h1>Our Services</h1>
          <p>Comprehensive thermal engineering solutions for your business success</p>
        </Container>
      </div>

      <section className="section-padding">
        <Container>
          <div className="section-title">
            <h2>Service Portfolio</h2>
            <p>Expert solutions across the complete thermal engineering lifecycle</p>
            <div className="mt-3 mb-4">
              <div style={{
                display: 'inline-block',
                padding: '8px 20px',
                background: '#f8fafc',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                fontSize: '0.9rem',
                color: '#64748b'
              }}>
                17+ Years of Excellence • 500+ Projects Delivered • 24/7 Support
              </div>
            </div>
          </div>
          <Row>
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </Row>
        </Container>
      </section>

      <section className="section-padding company-profile-section">
        <Container>
          <div className="section-title">
            <h2>Service Process Videos</h2>
            <p>See our professional service delivery in action</p>
          </div>
          
          {/* Featured Service Video */}
          <Row className="justify-content-center mb-5">
            <Col lg={10}>
              <div className="bg-white p-4 rounded shadow-sm">
                <div className="ratio ratio-16x9">
                  <iframe
                    src="https://www.youtube.com/embed/Sc1La25b4To?rel=0&modestbranding=1"
                    title="Allied Thermal Service Excellence"
                    allowFullScreen
                    style={{
                      borderRadius: '12px',
                      border: 'none'
                    }}
                  ></iframe>
                </div>
                <div className="text-center mt-3">
                  <p className="mb-2" style={{ fontSize: '1.2rem', fontWeight: '700', color: '#1e293b' }}>
                    🎥 Featured: Professional Service Delivery
                  </p>
                  <p className="text-muted">
                    Watch our expert team delivering comprehensive thermal engineering services
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Service Process Videos */}
          <Row>
            <Col lg={6} className="mb-4">
              <div className="bg-white p-3 rounded shadow-sm">
                <div className="ratio ratio-16x9">
                  <iframe
                    src="https://www.youtube.com/embed/DRv_UJVKLxc?rel=0&modestbranding=1"
                    title="Installation Process"
                    allowFullScreen
                    style={{ borderRadius: '8px', border: 'none' }}
                  ></iframe>
                </div>
                <div className="text-center mt-2">
                  <h6 style={{ color: '#1e293b', fontWeight: '600' }}>⚙️ Installation Process</h6>
                  <small className="text-muted">Professional installation and commissioning</small>
                </div>
              </div>
            </Col>
            
            <Col lg={6} className="mb-4">
              <div className="bg-white p-3 rounded shadow-sm">
                <div className="ratio ratio-16x9">
                  <iframe
                    src="https://www.youtube.com/embed/Vjjt97jVr04?rel=0&modestbranding=1"
                    title="Maintenance Services"
                    allowFullScreen
                    style={{ borderRadius: '8px', border: 'none' }}
                  ></iframe>
                </div>
                <div className="text-center mt-2">
                  <h6 style={{ color: '#1e293b', fontWeight: '600' }}>🔧 Maintenance Excellence</h6>
                  <small className="text-muted">Comprehensive maintenance and support services</small>
                </div>
              </div>
            </Col>
          </Row>
          
          <div className="text-center mt-4">
            <p style={{ fontSize: '1.1rem', color: '#64748b', fontStyle: 'italic' }}>
              📺 Real service delivery processes by Allied Thermal experts
            </p>
          </div>
        </Container>
      </section>

      <section className="section-padding">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <h2>Service Excellence</h2>
              <p className="mb-4">
                Our team of certified thermal engineers brings decades of experience 
                in designing, implementing, and maintaining thermal systems across 
                various industries.
              </p>
              <p className="mb-4">
                We follow rigorous quality standards and use cutting-edge technology 
                to deliver solutions that exceed expectations and industry benchmarks.
              </p>
              <div className="mt-4">
                <Button href="/contact-us" className="btn-primary-custom me-3">
                  Get Service Quote
                </Button>
                <Button href="/products" variant="outline-primary">
                  View Products
                </Button>
              </div>
            </Col>
            <Col lg={6}>
              <Row>
                <Col sm={6} className="mb-3">
                  <div className="stats-card">
                    <span className="stats-number">500+</span>
                    <p>Projects Completed</p>
                  </div>
                </Col>
                <Col sm={6} className="mb-3">
                  <div className="stats-card">
                    <span className="stats-number">99.8%</span>
                    <p>Uptime Guarantee</p>
                  </div>
                </Col>
                <Col sm={6} className="mb-3">
                  <div className="stats-card">
                    <span className="stats-number">24/7</span>
                    <p>Support Available</p>
                  </div>
                </Col>
                <Col sm={6} className="mb-3">
                  <div className="stats-card">
                    <span className="stats-number">15+</span>
                    <p>Years Experience</p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Service Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="me-2" style={{ fontSize: '1.5rem' }}>{selectedService?.icon}</span>
            {selectedService?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedService && (
            <>
              <Badge bg="primary" className="mb-3">{selectedService.category}</Badge>
              <p className="mb-4">{selectedService.description}</p>
              
              <Row>
                <Col md={6}>
                  <h6>Key Features:</h6>
                  <div className="mb-3">
                    {selectedService.features.map((feature, idx) => (
                      <Badge bg="secondary" className="me-2 mb-2" key={idx}>
                        ✓ {feature}
                      </Badge>
                    ))}
                  </div>
                </Col>
                <Col md={6}>
                  <h6>Benefits:</h6>
                  <div className="mb-3">
                    {selectedService.benefits.map((benefit, idx) => (
                      <Badge bg="success" className="me-2 mb-2" key={idx}>
                        ★ {benefit}
                      </Badge>
                    ))}
                  </div>
                </Col>
              </Row>
              
              <h6>Service Process:</h6>
              <div className="mb-3">
                {selectedService.process.map((step, idx) => (
                  <Badge bg="info" className="me-2 mb-2" key={idx}>
                    {idx + 1}. {step}
                  </Badge>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" href="/contact-us">
            Request Service
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Services;
